import { z } from "zod";

// Our model, as an agent on `zaistev-model` reaches it (ADR-023 §7, D670): the model door is the
// Messages API's own shape under `/api/agents/me/model`, so Claude Code calls it unchanged with
// its base URL pointed at the loopback. The door checks what it must and passes the rest on as
// the harness sent it, so a newer harness keeps working: the model, the size of the answer, and
// nothing that would reach past the egress proxy from the model's side (D674).

/** The largest answer one call may ask for, in tokens. */
export const MAX_MODEL_OUTPUT_TOKENS = 64_000;

/** A tool the harness itself runs, as the Messages API describes one: a name and its input's
 * schema. A server tool (web search, code execution, a fetch) runs on the model's side, out of
 * reach of the egress proxy's allow-list and priced apart, so it is refused. */
const modelToolSchema = z
  .looseObject({ name: z.string().min(1).max(128) })
  .refine((tool) => !("type" in tool) || tool["type"] === "custom", {
    message: "only tools the agent runs itself; a server tool is not offered on our model",
  });

/** One Messages API request, as Claude Code sends it. Unknown fields pass on as they came. */
export const modelRequestSchema = z
  .looseObject({
    model: z.string().min(1).max(100),
    max_tokens: z.number().int().min(1).max(MAX_MODEL_OUTPUT_TOKENS),
    messages: z.array(z.unknown()).min(1).max(10_000),
    stream: z.boolean().optional(),
    tools: z.array(modelToolSchema).max(200).optional(),
  })
  .refine((request) => !("mcp_servers" in request), {
    message: "MCP servers on the model's side are not offered on our model",
    path: ["mcp_servers"],
  })
  .refine((request) => !("container" in request), {
    message: "a code execution container is not offered on our model",
    path: ["container"],
  });
export type ModelRequest = z.infer<typeof modelRequestSchema>;

/** Counting a request's tokens: the same request, without the size of the answer. */
export const modelCountRequestSchema = z
  .looseObject({
    model: z.string().min(1).max(100),
    messages: z.array(z.unknown()).min(1).max(10_000),
    tools: z.array(modelToolSchema).max(200).optional(),
  })
  .refine((request) => !("mcp_servers" in request), {
    message: "MCP servers on the model's side are not offered on our model",
    path: ["mcp_servers"],
  });
export type ModelCountRequest = z.infer<typeof modelCountRequestSchema>;

/** The model door's input: the body, and `?beta=true`, which Claude Code adds to every call. */
export const modelMessagesInputSchema = z.object({
  beta: z.enum(["true", "false"]).optional(),
  body: modelRequestSchema,
});
export type ModelMessagesInput = z.infer<typeof modelMessagesInputSchema>;

export const modelCountTokensInputSchema = z.object({
  beta: z.enum(["true", "false"]).optional(),
  body: modelCountRequestSchema,
});
export type ModelCountTokensInput = z.infer<typeof modelCountTokensInputSchema>;

/** What the model answered, passed back as it came: its status, its type, the headers a client
 * acts on, and its body as it arrives (a stream of events, or one JSON document). */
export type ModelRelayAnswer = {
  readonly status: number;
  readonly contentType: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly body: AsyncIterable<string>;
};
